import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PaymentPausedScreen } from '@/components/PaymentPausedScreen';
import { CardRequiredScreen } from '@/components/trial/CardRequiredScreen';

// Mid-onboarding, the owner may step out to connect an ad account (Settings → Integrations and the OAuth return there) and come back.
// Paying must always be reachable — an expired trial that never finished onboarding would otherwise bounce /upgrade → /onboarding → lock screen.
const BILLING_PATHS = ['/upgrade', '/billing', '/pricing'];
const onboardingAllowed = (loc) => loc.pathname === '/onboarding'
  || BILLING_PATHS.some((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`))
  || (loc.pathname === '/settings' && /(tab=integrations|tab=billing|connect=|_connected=|_error=|switch=)/.test(loc.search || ''));

export const ProtectedRoute = ({ children, blockFreePartner = false }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Trial / subscription access is now governed server-side by the entitlement engine
  // (see backend `entitlements.py` + `TrialGate`). No client-side date math here.

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      const here = `${location.pathname}${location.search}${location.hash}`;
      let returning = false;
      try { returning = localStorage.getItem('veluro_returning') === '1'; } catch { /* ignore */ }
      const expired = returning ? '&expired=1' : '';
      navigate(here && here !== '/' ? `/login?next=${encodeURIComponent(here)}${expired}` : `/login${expired ? '?expired=1' : ''}`);
      return;
    }
    // Unpaid partner signups skip onboarding — they are locked until their plan checkout completes (/partner).
    // Fresh signups without a card see the trial-start gate instead (rendered below), never onboarding.
    if (user && !user.has_onboarded && !user.partner_payment_pending && !user.card_required && !onboardingAllowed(location)) {
      navigate('/onboarding');
    }
  }, [loading, isAuthenticated, user, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;
  // Plan picked, card not added yet: nothing runs until the Stripe trial checkout completes ($0 today).
  if (user?.card_required && !location.pathname.startsWith('/billing/trial-started')) {
    return <CardRequiredScreen />;
  }
  const freePlan = ['free', 'partner_free'].includes(user?.plan_level);
  if (user && !freePlan && (user.subscription_status === 'past_due' || user.subscription_status === 'unpaid') && !location.pathname.startsWith('/billing/update-card')) {
    return <PaymentPausedScreen />;
  }
  if (blockFreePartner && user?.plan_level === 'partner_free') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="free-partner-upgrade-gate">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-3">Upgrade to unlock this feature</h1>
          <p className="text-white/50 text-sm mb-6">Image and video generation isn't included in the free partner plan. Upgrade to a paid plan to unlock the full Veluro platform.</p>
          <button onClick={() => navigate('/home')} className="px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold">Back to Home</button>
        </div>
      </div>
    );
  }
  if (user && !user.has_onboarded && !user.partner_payment_pending && !user.card_required && !onboardingAllowed(location)) return null;

  return children;
};
